import { Fragment, type FunctionComponent, useEffect } from "react";
import { Hero } from "@App/Components/Hero";
import { ContactUs } from "@App/Components/ContactUs";
import { TriangleDivider } from "@App/Components/TriangleDivider";
import { Stepper } from "@App/Components/Stepper/Stepper";
import { useServices } from "@App/hooks/api/useServices";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useHome } from "@App/hooks/useHome";
import { ServiceStep } from "@App/Pages/Partner/Partials/ServiceStep";
import { Benefits } from "@App/Pages/Partner/Partials/Benefits";
import { LocationStepDefinition } from "@App/Components/molecules/whitelabel/home/stepDefinitions/LocationStepDefinition";
import { useLocationStepDefinition } from "@App/hooks/whitelabel/home/stepper/useLocationStepDefinition";
import { FontSize } from "@App/utils/tailwind/fontsize";
import { Step } from "@App/Components/Stepper/Step";
import { Headline } from "@App/Components/atoms/common/Headline";

export type HomeProps = {
  embedded?: boolean;
  onSubmit: () => void;
};

export const Home: FunctionComponent<HomeProps> = ({ embedded = false, onSubmit }: HomeProps) => {
  const location = useLocation();
  const { t } = useTranslation();

  // step definitions
  const locationStepProps = useLocationStepDefinition(onSubmit);

  const { loading, error, services } = useServices();

  const { currentStep, setCurrentStep, organization, service, selectService } = useHome();

  useEffect(() => {
    if (Number.parseInt(window.location.hash.substr(1)) > 0) {
      setCurrentStep(Number.parseInt(window.location.hash.substr(1)));
    }
  }, [location]);

  useEffect(() => {
    if (!service) {
      setCurrentStep(0);
    } else if (currentStep === 0) {
      setCurrentStep(1);
    }
  }, [service]);

  if (error) {
    console.log(error);
  }

  return (
    <Fragment>
      <Hero
        organization={organization}
        title={t("hero.title", { organization: organization.name })}
        subtitle={t("hero.subtitle", {
          organization: organization.name,
        })}
        backgroundImage={
          organization.settings?.hero.background_image_url ??
          "/images/brand/arval-banner-darkened.jpg"
        }
        backgroundPosition={"bg-center"}
        titleFontSize={FontSize["md:text-5xl"]}
      />
      <div className="bg-white px-5 pt-5 container">
        <Stepper currentStep={currentStep} onChange={setCurrentStep}>
          <Step
            title={t("Waehlen Sie einen Service.")}
            subtitle={service?.pivot.subtitle ? service.pivot.subtitle : null}
            completedTitle={"Service: "}
            completedGoBackLinkTitle={
              service
                ? service.pivot.display_name
                  ? service.pivot.display_name
                  : service.name
                : ""
            }
          >
            <ServiceStep
              service={service}
              services={services}
              loading={loading}
              error={error}
              selectService={selectService}
              organization={organization}
            />
          </Step>
          <LocationStepDefinition {...locationStepProps} />
        </Stepper>
        <div className="mt-24">
          <Headline type="h2">Service-Dokumente</Headline>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 py-4">
            <div>
              <p className="font-semibold text-sm leading-tight">
                Speditionskosten bei
                <br />
                Haustürabholung
              </p>
              <a
                href="https://werkstatt.repareo.de/storage/downloads?file=%2Fhome%2Fpartners%2Fsantander%2FLieferkosten_Spedition_Santander_Stand_122025.pdf&disk=repareoFilesharing"
                className="text-gray-500 text-sm hover:underline"
              >
                Herunterladen
              </a>
            </div>
            <div>
              <p className="font-semibold text-sm leading-tight">
                Schadenkatalog „Faire
                <br />
                Fahrzeugbewertung“
              </p>
              <a
                href="https://werkstatt.repareo.de/storage/downloads?file=%2Fhome%2Fpartners%2Fsantander%2FP250917_Rueckgabeschadenkatalog_SCL_01_web_RZ.pdf&disk=repareoFilesharing"
                className="text-gray-500 text-sm hover:underline"
              >
                Herunterladen
              </a>
            </div>
            <div>
              <p className="font-semibold text-sm leading-tight">
                Checkliste für
                <br />
                Fahrzeugrückgabe
              </p>
              <a
                href="https://werkstatt.repareo.de/storage/downloads?file=%2Fhome%2Fpartners%2Fsantander%2Fcheckliste_leasingrueckgabe.pdf&disk=repareoFilesharing"
                className="text-gray-500 text-sm hover:underline"
              >
                Herunterladen
              </a>
            </div>
            <div>
              <p className="font-semibold text-sm leading-tight">
                FAQs
                <br />
                Fahrzeugrückgabe
              </p>
              <a
                href="https://werkstatt.repareo.de/storage/downloads?file=%2Fhome%2Fpartners%2Fsantander%2Ffaqs_leasingrueckgabe.pdf&disk=repareoFilesharing"
                className="text-gray-500 text-sm hover:underline"
              >
                Herunterladen
              </a>
            </div>
          </div>
        </div>
      </div>
      {!embedded && (
        <div className="bg-neutral-100">
          <TriangleDivider direction="left" background={"bg-white"} />
          <div className="px-5 py-5 container md:pb-20">
            <div className="md:grid md:grid-cols-[50%_50%]">
              <Benefits organization={organization} />
              <div className="pt-5 md:pt-0 md:ml-12">
                <ContactUs />
              </div>
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
};
